import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { OutletLabelCategoriesEnum } from '../../core/enums/OutletLabelCategoriesEnum';
import { IOutletStoreArticle } from '../../core/interfaces';
import { ArticlesVariantsService } from '../../core/services';
import { debounceTime, finalize, switchMap } from 'rxjs/operators';
import { NotificationService } from '../../../../core/services';
import { Observable, Subject } from 'rxjs';
import { ISimpleListItem } from '../../core/interfaces/ISimpleListItem';

@Component({
    selector: 'app-outlet-store-articles-print-modal',
    templateUrl: './outlet-store-articles-print-modal.component.html',
    styleUrls: ['./outlet-store-articles-print-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class OutletStoreArticlesPrintModalComponent implements OnInit {

  @ViewChild('openPdfButtonElRef') openPdfButtonElRef: ElementRef<HTMLButtonElement>;

  public isVisible = false;
  public isConfirmLoading = false;
  public isLoading = false;
  public isArticlesSearchLoading = false;
  public labelKey: string = null;
  public articles: IOutletStoreArticle[] = [];
  public articleOptions: ISimpleListItem[] = [];
  public selectedVariantId: number = null;
  public copies = 1;
  public readonly title = 'Outlet-Store-Etiketten drucken';
  public readonly outletLabelCategoriesEnum = OutletLabelCategoriesEnum;

  private readonly searchArticleChange$ = new Subject<string>();

  constructor(
    private _cdr: ChangeDetectorRef,
    private readonly _articlesVariantsService: ArticlesVariantsService,
    private readonly _notificationService: NotificationService,
  ) {}

  public ngOnInit(): void {
    const articlesOptionList$: Observable<ISimpleListItem[]> = this.searchArticleChange$.pipe(
      debounceTime(500),
      switchMap(name => this._articlesVariantsService.getSimpleList(name))
    );
    articlesOptionList$.subscribe(data => {
      this.articleOptions = data;
      this.isArticlesSearchLoading = false;
      this._cdr.markForCheck();
    });
  }

  public showModal(articlesIds: number[]): void {
    this.isVisible = true;
    this.isLoading = false;
    this.isConfirmLoading = false;
    this.articles = [];
    this.selectedVariantId = null;
    this.labelKey = null;
    this.copies = 1;
    this.onArticleSearch('');

    if (articlesIds.length > 0) {
      this.loadArticles(articlesIds, true);
    }

    this._cdr.markForCheck();
  }

  public handleCancel(): void {
    this.isConfirmLoading = false;
    this.isVisible = false;
    this._cdr.markForCheck();
  }

  public handleOk() {
    if (this.articles.length === 0) {
      this._notificationService.pushError('Fehler', 'Bitte mindestens eine Artikelvariante auswählen.');
      return;
    }

    this.isConfirmLoading = true;
    const articles = this.articles.map(x => {
      return {
        ...x,
        weight: x.weight.toString()
      };
    });

    this._articlesVariantsService.generateOutletStoreLabel(articles, this.copies || 1).pipe(
      finalize(() => {
        this.isConfirmLoading = false;
        this._cdr.detectChanges();
      })
    ).subscribe(res => {
      this.labelKey = res.labelKey;
      setTimeout(() => this.openPdfButtonElRef.nativeElement.click());
    }, error => this._notificationService.pushError('Fehler', 'Fehler beim Erzeugen des Etiketts.'));
  }

  public onArticleSearch(value: string): void {
    this.isArticlesSearchLoading = true;
    this.searchArticleChange$.next(value);
    this._cdr.markForCheck();
  }

  public onVariantPicked(id: number): void {
    this.selectedVariantId = null;
    if (!id || this.articles.some(article => article.id === id)) {
      this._cdr.markForCheck();
      return;
    }

    this.loadArticles([id], false);
  }

  public removeArticle(article: IOutletStoreArticle): void {
    this.articles = this.articles.filter(x => x.id !== article.id);
    this._cdr.markForCheck();
  }

  public variantId(item: ISimpleListItem): number {
    return Number(item.id);
  }

  public priceChanged(article: IOutletStoreArticle): void {
    article.pricePer100 = article.weight !== '' ?
      this.getCalcedPrice(article.price, 100, Number(article.weight)) :
      '';
  }

  public pricePer100Changed(article: IOutletStoreArticle): void {
    article.price = this.getCalcedPrice(article.pricePer100, Number(article.weight), 100);
  }

  private loadArticles(articlesIds: number[], replace: boolean): void {
    this.isLoading = true;

    this._articlesVariantsService.getOutletStoreArticlesByIds(articlesIds).pipe(
      finalize(() => {
        this.isLoading = false;
        this._cdr.detectChanges();
      })
    ).subscribe(res => {
      const mapped = res.map(x => {

        if (!x.weight) {
          x.weight = '';
        }

        if (x.weight !== '') {
          x.pricePer100 = this.getCalcedPrice(x.price.toString(), 100, Number(x.weight));
        }

        return {
          ...x,
          price: this.getLocaleStringFromNumber(Number(x.price)),
          category: OutletLabelCategoriesEnum.BIG_TIN
        };
      });

      this.articles = replace
        ? mapped
        : [...this.articles, ...mapped.filter(item => !this.articles.some(existing => existing.id === item.id))];
    }, error => this._notificationService.pushError('Fehler', 'Fehler beim Abrufen von Artikeln.'));
  }

  private getCalcedPrice(price: string, weight1: number, weight2: number): string {
    return this.getLocaleStringFromNumber(
      (Number(price.replace(',', '.')) * weight1) / weight2
    );
  }

  private getLocaleStringFromNumber(val: number): string {
    return val.toLocaleString('de', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}
